import { useRef } from "react";
import { useInView } from "@/hooks/use-in-view";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs: { q: string; a: React.ReactNode }[] = [
  { q: "Combien de temps durent les travaux de carrosserie ?", a: "Cela dépend de l'étendue des dégâts. Un débosselage sans peinture (PDR) peut être réalisé en 1 à 3 heures seulement. Pour des travaux de tôlerie et peinture plus importants, comptez entre 3 et 10 jours selon la complexité. Nous vous fournissons un planning précis et transparent lors de l'établissement du devis, afin que vous puissiez organiser au mieux cette période." },
  { q: "Gérez-vous les démarches avec mon assurance ?", a: <>Absolument. Nous prenons en charge l'intégralité de la gestion administrative avec votre compagnie d'assurance. Après l'expertise de votre véhicule, nous établissons un devis détaillé accompagné de photos que nous transmettons directement à votre assureur. Vous n'avez qu'à nous fournir vos coordonnées d'assurance, et nous nous occupons du reste. Vous ne réglez que votre franchise. Le marché suisse de l'assurance est supervisé par la <a href="https://www.finma.ch/fr/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">FINMA</a>.</> },
  { q: "La teinte de peinture sera-t-elle exactement la même ?", a: "Oui, c'est notre garantie. Nous utilisons un spectrophotomètre de dernière génération qui analyse avec précision la couleur exacte de votre véhicule, en tenant compte de l'évolution naturelle de la teinte au fil du temps (exposition UV, vieillissement). Notre cabine de peinture pressurisée et nos techniques d'application professionnelles garantissent une correspondance colorimétrique parfaite, invisible à l'œil nu." },
  { q: "Le débosselage sans peinture (PDR) abîme-t-il la carrosserie ?", a: "Non, c'est justement tout l'inverse. Le débosselage sans peinture est une technique non-invasive qui préserve intégralement la peinture d'origine de votre véhicule. Notre spécialiste accède à la face interne du panneau et utilise des outils spéciaux pour exercer une pression progressive, permettant au métal de reprendre sa forme originale. Aucun produit chimique, aucun ponçage, aucune peinture : le résultat est totalement invisible et maintient la valeur de votre véhicule." },
  { q: "Quels sont vos délais d'intervention ?", a: "Nous comprenons l'urgence de remettre votre véhicule en circulation. Pour un débosselage sans peinture, nous pouvons souvent intervenir dans les 24 à 48 heures. Pour des travaux plus conséquents nécessitant tôlerie et peinture, nous planifions une intervention sous 3 à 5 jours ouvrés après validation du devis par votre assurance. En cas d'urgence, contactez-nous directement pour étudier les possibilités d'accélération." },
  { q: "Travaillez-vous avec toutes les assurances ?", a: <>Oui, nous collaborons avec l'ensemble des compagnies d'assurance présentes en Suisse : AXA, Allianz, Zurich, Generali, Mobilière, Helvetia, Baloise, et toutes les autres. En tant qu'atelier indépendant, nous garantissons une liberté totale de choix, conformément aux réglementations de la <a href="https://www.finma.ch/fr/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">FINMA</a>. Quel que soit votre assureur, nous gérons les démarches de A à Z.</> },
  { q: "Proposez-vous un devis gratuit ?", a: "Oui, l'établissement du devis est entièrement gratuit et sans engagement. Il vous suffit de nous contacter par téléphone, WhatsApp, ou de passer directement à l'atelier avec votre véhicule. Nous effectuons une expertise complète, prenons les photos nécessaires, et vous remettons un devis détaillé sous 24 heures. Vous décidez ensuite en toute tranquillité." },
  { q: "Utilisez-vous des pièces d'origine constructeur ?", a: "Nous privilégions systématiquement les pièces d'origine constructeur pour garantir un ajustement parfait, une durabilité optimale et le maintien de la garantie de votre véhicule. Dans certains cas, et toujours avec votre accord, nous pouvons proposer des pièces alternatives de qualité équivalente qui permettent de réduire les coûts tout en assurant une qualité irréprochable." },
];

const FaqSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="faq" className="py-24 bg-muted/40 relative">

      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Questions fréquentes</h2>
        </div>

        <div className="max-w-3xl mx-auto mb-12 grid gap-4 sm:grid-cols-3">
          <div className="glass-card rounded-lg p-5">
            <h3 className="text-sm font-semibold mb-2">Qu'est-ce que le débosselage sans peinture ?</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Le débosselage sans peinture est une technique non-invasive qui retire les bosses sans toucher à la peinture d'origine, en 1 à 3 heures.
            </p>
          </div>
          <div className="glass-card rounded-lg p-5">
            <h3 className="text-sm font-semibold mb-2">Combien coûte une réparation ?</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Le devis est gratuit chez ProImpact à Genève. En cas de sinistre couvert, vous ne payez que votre franchise, nous facturons directement votre assureur.
            </p>
          </div>
          <div className="glass-card rounded-lg p-5">
            <h3 className="text-sm font-semibold mb-2">Quelles assurances ?</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              ProImpact gère toutes les assurances suisses : AXA, Allianz, Zurich, Mobilière, Generali, Helvetia, Baloise et autres.
            </p>
          </div>
        </div>

        {/* Glossaire visible - citabilité IA */}
        <div className="max-w-3xl mx-auto mb-12 glass-card rounded-lg p-6">
          <h3 className="text-base font-semibold mb-4">Glossaire carrosserie</h3>
          <dl className="grid gap-3 sm:grid-cols-2 text-sm">
            <div>
              <dt className="font-semibold text-foreground">Tôlerie automobile</dt>
              <dd className="text-muted-foreground">Réparation et redressage des éléments métalliques de la carrosserie après un choc.</dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">Débosselage sans peinture</dt>
              <dd className="text-muted-foreground">Technique de redressage qui préserve la peinture d'origine, idéale pour la grêle et les petits chocs.</dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">Spectrophotomètre</dt>
              <dd className="text-muted-foreground">Appareil qui analyse la teinte exacte de votre véhicule pour une correspondance parfaite de la peinture.</dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">Franchise d'assurance</dt>
              <dd className="text-muted-foreground">Part des frais qui reste à votre charge en cas de sinistre couvert par votre assurance.</dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">Pièce OEM</dt>
              <dd className="text-muted-foreground">Pièce d'origine constructeur, identique à celle montée en usine, garantissant ajustement et durabilité.</dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">Detailing premium</dt>
              <dd className="text-muted-foreground">Remise à neuf esthétique complète : polissage, traitement céramique, rénovation des optiques.</dd>
            </div>
          </dl>
        </div>

        <div
          ref={ref}
          className={`max-w-3xl mx-auto reveal ${isInView ? "in-view" : ""}`}
          style={{ transitionDelay: "0.1s" }}
        >
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="glass-card rounded-lg px-6"
              >
                <AccordionTrigger className="text-left text-sm sm:text-base font-semibold hover:no-underline">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* FAQ texte plein pour crawlers et IA - invisible visuellement */}
        <div className="sr-only">
          {faqs.map((faq, i) => (
            <div key={`sr-${i}`}>
              <h3>{faq.q}</h3>
              <p>{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
